import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AccessNotice,
  ErrorBanner,
  GroupCard,
  ListSkeleton,
  MainButton,
  SectionHeader,
  useToast,
} from '../components';
import {
  useAddMember,
  useCreateInvite,
  useList,
  useMembers,
  useRemoveMember,
} from '../api/queries';
import { ApiError, isAccessError } from '../api/client';
import { displayName, initialOf } from '../api/format';
import { useSurface } from '../hooks/useSurface';
import type { Invite, Role } from '../api/types';

/**
 * Invite link + members + direct grant.
 *
 * The deep link is **whatever `POST /api/lists/{id}/invites` returned**. The
 * `inv_` start-payload prefix belongs to the bot (`sharing.py`), and the design
 * mock's `join_` would produce a link that redeems as nothing at all, with no
 * error anywhere — so the UI never assembles a t.me link and does not know the
 * prefix. The only link built here is the app's own `/redeem/:token` route,
 * which is this app's URL space, not the bot's.
 */
export function ShareScreen({ listId }: { listId: number }) {
  const navigate = useNavigate();
  const { isDesktop, isTelegram } = useSurface();
  const { showToast } = useToast();
  const { data: list, error: listError } = useList(listId);
  const { data: members, isPending, isError, error, refetch } = useMembers(listId);
  const createInvite = useCreateInvite(listId);
  const addMember = useAddMember(listId);
  const removeMember = useRemoveMember(listId);

  const [role, setRole] = useState<Role>('editor');
  const [invite, setInvite] = useState<Invite | null>(null);
  const [grantId, setGrantId] = useState('');
  const [grantRole, setGrantRole] = useState<Role>('editor');

  const accessError = isAccessError(error) ? error : isAccessError(listError) ? listError : null;

  async function copy(text: string, label: string) {
    try {
      await navigator.clipboard.writeText(text);
      showToast(`${label} copied`);
    } catch {
      // Clipboard access is denied in some Telegram webviews and on insecure
      // origins; the link stays selectable on screen either way.
      showToast('Copy failed — select the link instead');
    }
  }

  const copyLink = () => (invite ? copy(invite.url, 'Invite link') : Promise.resolve());

  // The app's own web fallback, for an invite opened in a browser rather than
  // in Telegram. `BASE_URL` is `/` in dev and `/manager/` on Pages, and always
  // has both slashes, so this needs no joining logic.
  const webLink = invite
    ? `${window.location.origin}${import.meta.env.BASE_URL}redeem/${invite.token}`
    : null;

  const apiMessage = (err: unknown, fallback: string) =>
    err instanceof ApiError ? err.message : fallback;

  if (accessError) {
    return (
      <div className={isDesktop ? 'screen-content content' : 'screen-content'}>
        <AccessNotice
          kind={accessError.isForbidden ? 'forbidden' : 'missing'}
          onBack={() => navigate('/', { replace: true })}
        />
      </div>
    );
  }

  return (
    <>
      {isDesktop ? (
        <div className="detail-title">
          <span className="detail-title__text">Share “{list?.name ?? ''}”</span>
        </div>
      ) : null}

      {isError ? <ErrorBanner message={error.message} onRetry={() => void refetch()} /> : null}

      <div className={isDesktop ? 'screen-content content' : 'screen-content'}>
        {isPending ? (
          <ListSkeleton />
        ) : (
          <div style={{ padding: '12px 0 30px' }}>
            <SectionHeader>Invite link</SectionHeader>
            <GroupCard>
              <div className="share__pad">
                <div className="share__label">Anyone with the link joins as:</div>
                <div className="share__roleTabs">
                  {(['editor', 'viewer'] as const).map((r) => (
                    <button
                      key={r}
                      type="button"
                      className={r === role ? 'share__roleTab is-active' : 'share__roleTab'}
                      onClick={() => {
                        setRole(r);
                        // A minted token carries its role; changing the role
                        // has to mint a new one rather than relabel this link.
                        setInvite(null);
                      }}
                    >
                      {r === 'editor' ? 'Editor' : 'Viewer'}
                    </button>
                  ))}
                </div>
              </div>

              {invite ? (
                <>
                  <div className="share__linkRow">
                    <span className="share__link">{invite.url}</span>
                    <button type="button" className="share__copy" onClick={() => void copyLink()}>
                      Copy
                    </button>
                  </div>
                  {webLink ? (
                    <div className="share__linkRow">
                      <span className="share__link">{webLink}</span>
                      <button
                        type="button"
                        className="share__copy"
                        onClick={() => void copy(webLink, 'Web link')}
                      >
                        Copy
                      </button>
                    </div>
                  ) : null}
                  <div className="share__note">
                    Either link works once, then expires. The first opens the bot in Telegram; the
                    second opens this app in a browser.
                  </div>
                </>
              ) : (
                <div className="share__generateWrap">
                  <button
                    type="button"
                    className="share__generate"
                    disabled={createInvite.isPending}
                    onClick={() =>
                      createInvite.mutate(role, {
                        onSuccess: setInvite,
                        onError: (err) => showToast(apiMessage(err, "Couldn't create a link")),
                      })
                    }
                  >
                    {createInvite.isPending ? 'Generating…' : 'Generate link'}
                  </button>
                </div>
              )}
            </GroupCard>

            <SectionHeader>Members</SectionHeader>
            <GroupCard>
              {(members ?? []).map((member) => {
                // Not a server field: the owner is not a row in `list_members`
                // at all, so "can this be removed" is exactly "is this not the
                // owner".
                const removable = member.role !== 'owner';
                const name = displayName(member.name, member.userId);
                return (
                  <div className="member-row" key={member.userId}>
                    <div
                      className="avatar avatar--member"
                      style={{ background: avatarColor(member.userId) }}
                    >
                      {initialOf(name)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="member-row__name">{name}</div>
                      <div className="member-row__role">{member.role}</div>
                    </div>
                    {removable ? (
                      <button
                        type="button"
                        className="member-row__remove"
                        onClick={() =>
                          removeMember.mutate(member.userId, {
                            onSuccess: () => showToast(`${name} removed`),
                            onError: (err) =>
                              showToast(apiMessage(err, "Couldn't remove that member")),
                          })
                        }
                      >
                        Remove
                      </button>
                    ) : null}
                  </div>
                );
              })}
            </GroupCard>

            <SectionHeader>Direct grant by user ID</SectionHeader>
            <GroupCard>
              <div className="share__pad">
                <div className="share__label">Grant as:</div>
                <div className="share__roleTabs">
                  {(['editor', 'viewer'] as const).map((r) => (
                    <button
                      key={r}
                      type="button"
                      className={r === grantRole ? 'share__roleTab is-active' : 'share__roleTab'}
                      onClick={() => setGrantRole(r)}
                    >
                      {r === 'editor' ? 'Editor' : 'Viewer'}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grant-row">
                <input
                  className="grant-row__input"
                  value={grantId}
                  inputMode="numeric"
                  placeholder="numeric user id…"
                  aria-label="Telegram user id"
                  onChange={(e) => setGrantId(e.target.value.replace(/[^\d]/g, ''))}
                />
                <button
                  type="button"
                  className="grant-row__button"
                  disabled={!grantId.trim() || addMember.isPending}
                  onClick={() => {
                    const userId = Number(grantId.trim());
                    if (!Number.isSafeInteger(userId) || userId <= 0) {
                      showToast('That is not a Telegram user id');
                      return;
                    }
                    addMember.mutate(
                      { userId, role: grantRole },
                      {
                        onSuccess: () => {
                          showToast(`Granted ${grantRole} access`);
                          setGrantId('');
                        },
                        // 400 here usually means "that user already owns this
                        // list" — `service.add_member` refuses that outright.
                        onError: (err) => showToast(apiMessage(err, "Couldn't grant access")),
                      },
                    );
                  }}
                >
                  {addMember.isPending ? 'Granting…' : 'Grant'}
                </button>
              </div>
            </GroupCard>
          </div>
        )}
      </div>

      {isTelegram && invite ? <MainButton label="Copy link" onClick={() => void copyLink()} /> : null}
    </>
  );
}

const AVATAR_COLORS = ['#3390ec', '#e17076', '#7bc862', '#e5ca77', '#a695e7'];

function avatarColor(seed: number): string {
  return AVATAR_COLORS[Math.abs(seed) % AVATAR_COLORS.length];
}
